import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { TrendingUp, Tag, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Sidebar() {
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [trending, setTrending] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/tags").then(r => setTags(r.data)).catch(() => {});
    api.get("/articles?limit=5").then(r => setTrending(r.data.articles || [])).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await api.post("/newsletter/subscribe", { email: email.trim() });
      toast.success("Subscribed successfully!");
      setEmail("");
    } catch {
      toast.error("Failed to subscribe");
    }
  };

  return (
    <aside className="flex flex-col gap-8" data-testid="sidebar">
      {/* Trending */}
      <div className="border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-[#1E88E5]" />
          <h3 className="font-sans font-semibold text-sm uppercase tracking-[0.15em] text-foreground">Trending</h3>
        </div>
        <div className="space-y-4">
          {trending.slice(0, 5).map((article, i) => (
            <div key={article.id} className="flex gap-3">
              <span className="text-2xl font-serif font-bold text-slate-200 dark:text-slate-700 leading-none">{String(i + 1).padStart(2, "0")}</span>
              <Link to={`/article/${article.id}`} className="text-sm font-sans font-medium text-foreground hover:text-[#1E88E5] transition-colors leading-snug line-clamp-2" data-testid={`trending-article-${i}`}>
                {article.title}
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className="border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-6">
        <h3 className="font-sans font-semibold text-sm uppercase tracking-[0.15em] text-foreground mb-4">Categories</h3>
        <div className="space-y-2">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="flex items-center justify-between py-2 text-sm font-sans text-muted-foreground hover:text-foreground transition-colors group"
              data-testid={`sidebar-cat-${cat.slug}`}
            >
              <span>{cat.name}</span>
              <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </div>
      </div>

      {/* Newsletter */}
      <div className="border border-[#1E88E5] bg-[#1E88E5]/5 dark:bg-[#1E88E5]/10 p-6">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="h-4 w-4 text-[#1E88E5]" />
          <h3 className="font-sans font-semibold text-sm uppercase tracking-[0.15em] text-foreground">Newsletter</h3>
        </div>
        <p className="text-xs font-sans text-muted-foreground mb-4">
          Get the latest medical news delivered to your inbox weekly.
        </p>
        <form onSubmit={subscribe} className="flex gap-2">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            type="email"
            className="text-sm h-9"
            data-testid="newsletter-email"
          />
          <Button type="submit" size="sm" className="bg-[#1E88E5] hover:bg-[#1565C0] h-9 px-3 shrink-0" data-testid="newsletter-subscribe">
            <Mail className="h-4 w-4" />
          </Button>
        </form>
      </div>

      <Separator />

      {/* Tags */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Tag className="h-4 w-4 text-[#26A69A]" />
          <h3 className="font-sans font-semibold text-sm uppercase tracking-[0.15em] text-foreground">Popular Tags</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Badge
              key={tag.id}
              variant="outline"
              className="font-sans text-xs cursor-pointer hover:bg-[#1E88E5] hover:text-white hover:border-[#1E88E5] transition-colors"
              data-testid={`tag-${tag.slug}`}
            >
              {tag.name}
            </Badge>
          ))}
        </div>
      </div>
    </aside>
  );
}
